import React, { useState, useEffect } from 'react';

const ServiceForm = ({ service, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    base_price: '',
    duration_minutes: '',
    labor_included: true,
    active: true
  });

  const [errors, setErrors] = useState({});

  const categories = [
    'Entretien',
    'Réparation',
    'Diagnostic',
    'Carrosserie',
    'Pneumatiques',
    'Climatisation',
    'Électronique'
  ];

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || '',
        category: service.category || '',
        description: service.description || '',
        base_price: service.base_price || '',
        duration_minutes: service.duration_minutes || '',
        labor_included: service.labor_included !== undefined ? service.labor_included : true,
        active: service.active !== undefined ? service.active : true
      });
    }
  }, [service]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for the changed field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom du service est requis';
    }

    if (!formData.category) {
      newErrors.category = 'La catégorie est requise';
    }

    if (!formData.base_price || isNaN(formData.base_price) || parseFloat(formData.base_price) < 0) {
      newErrors.base_price = 'Le prix doit être un nombre positif';
    }

    if (formData.duration_minutes && (isNaN(formData.duration_minutes) || parseInt(formData.duration_minutes) < 0)) {
      newErrors.duration_minutes = 'La durée doit être un nombre positif (en minutes)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitData = {
        ...formData,
        base_price: parseFloat(formData.base_price),
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null
      };
      onSubmit(submitData);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    }
    return `${mins}min`;
  };

  return (
    <div className="service-form-overlay">
      <div className="service-form-container">
        <div className="service-form-header">
          <h2>{service ? 'Modifier le service' : 'Nouveau service'}</h2>
          <button className="close-btn" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="service-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Nom du service *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Vidange complète, Changement plaquettes..."
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="category">Catégorie *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={errors.category ? 'error' : ''}
              >
                <option value="">Sélectionner une catégorie...</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {errors.category && <span className="error-text">{errors.category}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="base_price">Prix de base (€) *</label>
              <input
                type="number"
                id="base_price"
                name="base_price"
                value={formData.base_price}
                onChange={handleChange}
                placeholder="45.00"
                min="0"
                step="0.01"
                className={errors.base_price ? 'error' : ''}
              />
              {errors.base_price && <span className="error-text">{errors.base_price}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="duration_minutes">
                Durée estimée (minutes)
                {formData.duration_minutes && (
                  <span className="duration-preview"> - {formatDuration(parseInt(formData.duration_minutes))}</span>
                )}
              </label>
              <input
                type="number"
                id="duration_minutes"
                name="duration_minutes"
                value={formData.duration_minutes}
                onChange={handleChange}
                placeholder="30"
                min="0"
                className={errors.duration_minutes ? 'error' : ''}
              />
              {errors.duration_minutes && <span className="error-text">{errors.duration_minutes}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Description détaillée du service, ce qui est inclus..."
              rows="4"
            />
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="labor_included"
                  checked={formData.labor_included}
                  onChange={handleChange}
                />
                <span className="checkbox-text">Main d'œuvre incluse</span>
              </label>
              <small className="help-text">
                Cochez si le prix inclut la main d'œuvre
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                />
                <span className="checkbox-text">Service actif</span>
              </label>
              <small className="help-text">
                Les services inactifs ne sont pas proposés aux clients
              </small>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? 'Enregistrement...' : (service ? 'Mettre à jour' : 'Créer le service')}
            </button>
          </div>
        </form>

        <style jsx>{`
          .service-form-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .service-form-container {
            background: white;
            border-radius: 12px;
            width: 100%;
            max-width: 700px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .service-form-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 24px 0 24px;
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 24px;
          }

          .service-form-header h2 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: #1f2937;
          }

          .close-btn {
            background: none;
            border: none;
            color: #6b7280;
            cursor: pointer;
            padding: 8px;
            border-radius: 6px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: #f3f4f6;
            color: #374151;
          }

          .service-form {
            padding: 0 24px 24px 24px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 16px;
          }

          @media (max-width: 640px) {
            .form-row {
              grid-template-columns: 1fr;
            }
          }

          .form-group {
            position: relative;
            margin-bottom: 16px;
          }

          .form-group label {
            display: block;
            margin-bottom: 6px;
            font-weight: 500;
            color: #374151;
            font-size: 0.9rem;
          }

          .duration-preview {
            color: #3b82f6;
            font-weight: 400;
            font-size: 0.8rem;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            width: 100%;
            padding: 12px 16px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            font-size: 0.9rem;
            transition: all 0.2s ease;
            box-sizing: border-box;
          }

          .form-group input:focus,
          .form-group select:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .form-group input.error,
          .form-group select.error,
          .form-group textarea.error {
            border-color: #dc2626;
          }

          .form-group textarea {
            resize: vertical;
            min-height: 100px;
          }

          .checkbox-group {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .checkbox-label {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            font-weight: 500;
            color: #374151;
          }

          .checkbox-label input[type="checkbox"] {
            width: auto;
            margin: 0;
          }

          .checkbox-text {
            font-size: 0.9rem;
          }

          .help-text {
            font-size: 0.8rem;
            color: #6b7280;
            margin-top: 4px;
          }

          .error-text {
            color: #dc2626;
            font-size: 0.8rem;
            margin-top: 4px;
            display: block;
          }

          .form-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 32px;
            padding-top: 24px;
            border-top: 1px solid #e5e7eb;
          }

          .btn-cancel,
          .btn-submit {
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
            font-size: 0.9rem;
          }

          .btn-cancel {
            background: #f3f4f6;
            color: #374151;
          }

          .btn-cancel:hover {
            background: #e5e7eb;
          }

          .btn-submit {
            background: #3b82f6;
            color: white;
          }

          .btn-submit:hover:not(:disabled) {
            background: #2563eb;
          }

          .btn-submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}</style>
      </div>
    </div>
  );
};

export default ServiceForm;
